/**
 * Locations with mixed electricity data sources that are under review
 * These locations have data from multiple utilities (e.g., eGrid and PG&E),
 * which can cause data quality issues and strange results.
 * 
 * Data sources by location:
 * - Anaheim (602000): eGrid, SCE
 * - Belvedere (605164): PG&E, SCE
 * - Colton (614890): eGrid, SCE
 * - Corona (616350): eGrid, SCE
 * - Eastvale (621230): NREL, SCE
 * - Glendale (630000): eGrid, SCE
 * - Los Angeles (644000): eGrid, SCE
 * - McFarland (652694): PG&E, SCE
 * - Oakdale (652694): eGrid, PG&E (Note: Same geofips as McFarland - may need verification)
 * - Riverside (662000): eGrid, SCE
 * - San Francisco (667000): eGrid, PG&E
 */

export const ELECTRICITY_DATA_UNDER_REVIEW_GEOFIPS = [
  602000,  // Anaheim - eGrid, SCE
  605164,  // Belvedere - PG&E, SCE
  614890,  // Colton - eGrid, SCE
  616350,  // Corona - eGrid, SCE
  621230,  // Eastvale - NREL, SCE
  630000,  // Glendale - eGrid, SCE
  644000,  // Los Angeles - eGrid, SCE
  652694,  // McFarland/Oakdale - Mixed sources (Note: Both locations may share same geofips - needs verification)
  662000,  // Riverside - eGrid, SCE
  667000,  // San Francisco - eGrid, PG&E
] as const;

export const ELECTRICITY_DATA_UNDER_REVIEW_LOCATIONS: Record<number, string> = {
  602000: 'Anaheim',
  605164: 'Belvedere',
  614890: 'Colton',
  616350: 'Corona',
  621230: 'Eastvale',
  630000: 'Glendale',
  644000: 'Los Angeles',
  652694: 'McFarland/Oakdale', // Note: Both locations listed with same geofips - may need verification
  662000: 'Riverside',
  667000: 'San Francisco',
};

// Location name to geofips mapping for fallback lookup
// Supports variations like "San Francisco" and "San Francisco County"
export const LOCATION_NAME_TO_GEOFIPS: Record<string, number> = {
  'anaheim': 602000,
  'belvedere': 605164,
  'colton': 614890,
  'corona': 616350,
  'eastvale': 621230,
  'glendale': 630000,
  'los angeles': 644000,
  'mcfarland': 652694,
  'oakdale': 652694,
  'riverside': 662000,
  'san francisco': 667000,
  'san francisco county': 667000, // Also handle county variation
};

/**
 * Check if a location (by geofips) has electricity data under review
 */
export function isElectricityDataUnderReview(geofips: number | null | undefined): boolean {
  if (!geofips) return false;
  return ELECTRICITY_DATA_UNDER_REVIEW_GEOFIPS.includes(geofips as any);
}

/**
 * Check if a location (by name) has electricity data under review
 * Fallback method when geofips is not available
 * Handles variations like "San Francisco" and "San Francisco County"
 */
export function isElectricityDataUnderReviewByName(locationName: string | null | undefined): boolean {
  if (!locationName) return false;
  let normalizedName = locationName.toLowerCase().trim();
  
  // Remove " County" suffix for matching
  normalizedName = normalizedName.replace(/\s+county\s*$/i, '');
  
  return normalizedName in LOCATION_NAME_TO_GEOFIPS;
}

/**
 * Get the location name for a geofips that's under review
 */
export function getLocationNameUnderReview(geofips: number | null | undefined): string | null {
  if (!geofips) return null;
  return ELECTRICITY_DATA_UNDER_REVIEW_LOCATIONS[geofips] || null;
}

/**
 * Get geofips from location name (for fallback lookup)
 */
export function getGeofipsFromLocationName(locationName: string | null | undefined): number | null {
  if (!locationName) return null;
  const normalizedName = locationName.toLowerCase().trim();
  return LOCATION_NAME_TO_GEOFIPS[normalizedName] || null;
}

